/**
 * Central Pinia store for the currently authenticated tenant theme.
 *
 * The store keeps both the last database snapshot and the mutable working copy
 * in memory so components can:
 * - access the tenant theme from one place
 * - detect whether local changes are still in sync with the database
 * - save the current working copy back to the backend
 * - survive page reloads through a tenant-scoped localStorage cache
 */

import { defineStore } from 'pinia'
import axiosInstance from '@/plugins/axios'
import { useSessionStore } from '@/store/sessionStore'

export interface ThemeSettingRecord {
  key: string
  help?: string
  category?: string
  fieldType?: string
  options?: any[]
  defaultValue?: any
  required?: boolean
  value?: any
  cssType?: string
  cssClass?: string
  langId?: string
  helpImage?: string
  hidden?: boolean
  moduleKey?: string
  isModuleSetting?: boolean
  title?: string
  order?: number
  [key: string]: any
}

export interface ThemeSettingChange {
  key: string
  title: string
  category: string
  fieldType?: string
  previousValue: any
  nextValue: any
}

type ThemeSettingsMap = Record<string, ThemeSettingRecord>
type ThemeSyncStatus = 'idle' | 'loading' | 'saving' | 'in_sync' | 'out_of_sync' | 'error'

interface ThemeApiResponse {
  _id?: string
  id?: string
  name?: string
  styles?: Record<string, any>
  availableFonts?: string[]
  moduleThemeSettings?: Record<string, any>
  updatedAt?: string
}

interface ThemeStoreCache {
  tenantId: string
  themeId: string | null
  themeName: string | null
  themeSettings: ThemeSettingsMap
  databaseThemeSettings: ThemeSettingsMap
  themeTemplate: ThemeSettingRecord[]
  availableFonts: string[]
  themeUpdatedAt: string | null
  lastLoadedAt: string | null
  lastSyncedAt: string | null
}

interface ThemeState {
  tenantId: string | null
  themeId: string | null
  themeName: string | null
  themeSettings: ThemeSettingsMap
  databaseThemeSettings: ThemeSettingsMap
  themeTemplate: ThemeSettingRecord[]
  availableFonts: string[]
  loading: boolean
  saving: boolean
  isLoaded: boolean
  error: string | null
  themeUpdatedAt: string | null
  lastLoadedAt: string | null
  lastSyncedAt: string | null
}

const STORAGE_PREFIX = 'airin-admin-theme-store'

const clonePlain = <T>(value: T): T => {
  if (value === null || value === undefined) {
    return value
  }

  return JSON.parse(JSON.stringify(value)) as T
}

const sortDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortDeep)
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce(
        (result, key) => {
          result[key] = sortDeep((value as Record<string, unknown>)[key])
          return result
        },
        {} as Record<string, unknown>
      )
  }

  return value
}

const areThemeSettingsEqual = (left: ThemeSettingsMap, right: ThemeSettingsMap): boolean => {
  return JSON.stringify(sortDeep(left)) === JSON.stringify(sortDeep(right))
}

const areSettingValuesEqual = (left: unknown, right: unknown): boolean => {
  return JSON.stringify(sortDeep(left)) === JSON.stringify(sortDeep(right))
}

const getCacheKey = (tenantId: string) => `${STORAGE_PREFIX}:${tenantId}`

const readCache = (tenantId: string): ThemeStoreCache | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = window.localStorage.getItem(getCacheKey(tenantId))
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as ThemeStoreCache
  } catch {
    window.localStorage.removeItem(getCacheKey(tenantId))
    return null
  }
}

const writeCache = (cache: ThemeStoreCache) => {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(getCacheKey(cache.tenantId), JSON.stringify(cache))
}

const removeCache = (tenantId: string | null) => {
  if (typeof window === 'undefined' || !tenantId) {
    return
  }

  window.localStorage.removeItem(getCacheKey(tenantId))
}

const normalizeSetting = (key: string, value: any): ThemeSettingRecord => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      ...clonePlain(value),
      key: value.key ?? key
    }
  }

  return {
    key,
    value: clonePlain(value)
  }
}

const normalizeSettingsMap = (
  settings: Record<string, any> | null | undefined
): ThemeSettingsMap => {
  const normalized: ThemeSettingsMap = {}

  for (const [key, value] of Object.entries(settings ?? {})) {
    normalized[key] = normalizeSetting(key, value)
  }

  return normalized
}

const applyTemplateDefaults = (
  settings: ThemeSettingsMap,
  template: ThemeSettingRecord[]
): ThemeSettingsMap => {
  const nextSettings = clonePlain(settings)

  for (const templateSetting of template) {
    const key = templateSetting.key
    const existingSetting = nextSettings[key]

    if (existingSetting) {
      nextSettings[key] = {
        ...templateSetting,
        ...existingSetting,
        key
      }
      continue
    }

    nextSettings[key] = {
      ...clonePlain(templateSetting),
      key,
      value: clonePlain(templateSetting.defaultValue ?? null)
    }
  }

  return nextSettings
}

const getTenantIdFromSession = (): string | null => {
  const sessionStore = useSessionStore()
  const tenant = sessionStore.tenant as Record<string, any> | null
  const user = sessionStore.user as Record<string, any> | null

  return tenant?.id ?? tenant?._id ?? user?.tenant ?? null
}

const buildSavePayload = (
  themeSettings: ThemeSettingsMap,
  themeTemplate: ThemeSettingRecord[]
): {
  themeSettings: ThemeSettingsMap
  moduleThemeSettings: Record<string, ThemeSettingsMap>
} => {
  const regularSettings: ThemeSettingsMap = {}
  const groupedModuleSettings: Record<string, ThemeSettingsMap> = {}
  const templateByKey = new Map(themeTemplate.map((setting) => [setting.key, setting]))

  for (const [key, setting] of Object.entries(themeSettings)) {
    const templateSetting = templateByKey.get(key)
    const moduleKey = templateSetting?.moduleKey ?? setting.moduleKey
    const isModuleSetting = templateSetting?.isModuleSetting ?? setting.isModuleSetting
    const plainSetting = clonePlain(setting)

    if (isModuleSetting && moduleKey) {
      if (!groupedModuleSettings[moduleKey]) {
        groupedModuleSettings[moduleKey] = {}
      }

      groupedModuleSettings[moduleKey][key] = plainSetting
      continue
    }

    regularSettings[key] = plainSetting
  }

  return {
    themeSettings: regularSettings,
    moduleThemeSettings: groupedModuleSettings
  }
}

export const useThemeStore = defineStore('theme', {
  state: (): ThemeState => ({
    tenantId: null,
    themeId: null,
    themeName: null,
    themeSettings: {},
    databaseThemeSettings: {},
    themeTemplate: [],
    availableFonts: [],
    loading: false,
    saving: false,
    isLoaded: false,
    error: null,
    themeUpdatedAt: null,
    lastLoadedAt: null,
    lastSyncedAt: null
  }),

  getters: {
    currentTenantId: (state): string | null => {
      return getTenantIdFromSession() ?? state.tenantId
    },

    hasTheme: (state): boolean => {
      return Object.keys(state.themeSettings).length > 0
    },

    hasUnsavedChanges(state): boolean {
      if (!this.isLoaded) {
        return false
      }

      return !areThemeSettingsEqual(state.themeSettings, state.databaseThemeSettings)
    },

    isInSync(): boolean {
      return this.isLoaded && !this.hasUnsavedChanges
    },

    syncStatus(state): ThemeSyncStatus {
      if (state.error) {
        return 'error'
      }

      if (state.saving) {
        return 'saving'
      }

      if (state.loading) {
        return 'loading'
      }

      if (!state.isLoaded) {
        return 'idle'
      }

      return this.hasUnsavedChanges ? 'out_of_sync' : 'in_sync'
    },

    changedSettings(state): ThemeSettingChange[] {
      const templateByKey = new Map(state.themeTemplate.map((setting) => [setting.key, setting]))
      const keys = new Set([
        ...Object.keys(state.databaseThemeSettings),
        ...Object.keys(state.themeSettings),
        ...state.themeTemplate.map((setting) => setting.key)
      ])
      const changes: ThemeSettingChange[] = []

      for (const key of keys) {
        const templateSetting = templateByKey.get(key)
        const previousSetting = state.databaseThemeSettings[key]
        const nextSetting = state.themeSettings[key]
        const previousValue = previousSetting?.value
        const nextValue = nextSetting?.value

        if (areSettingValuesEqual(previousValue, nextValue)) {
          continue
        }

        changes.push({
          key,
          title: String(
            nextSetting?.title ?? previousSetting?.title ?? templateSetting?.title ?? key
          ),
          category: String(
            nextSetting?.category ??
              previousSetting?.category ??
              templateSetting?.category ??
              'Uncategorized'
          ),
          fieldType:
            nextSetting?.fieldType ?? previousSetting?.fieldType ?? templateSetting?.fieldType,
          previousValue: clonePlain(previousValue),
          nextValue: clonePlain(nextValue)
        })
      }

      return changes.sort((left, right) => {
        if (left.category === right.category) {
          return left.title.localeCompare(right.title)
        }

        return left.category.localeCompare(right.category)
      })
    },

    changedSettingsCount(): number {
      return this.changedSettings.length
    },

    getThemeSetting:
      (state) =>
      (key: string): ThemeSettingRecord | null => {
        return state.themeSettings[key] ?? null
      },

    getSetting:
      (state) =>
      (key: string): ThemeSettingRecord | null => {
        return state.themeSettings[key] ?? null
      },

    getThemeValue:
      (state) =>
      (key: string): any => {
        return state.themeSettings[key]?.value
      },

    getSettingValue:
      (state) =>
      (key: string): any => {
        return state.themeSettings[key]?.value
      }
  },

  actions: {
    hydrateFromCache(tenantId: string): boolean {
      const cachedTheme = readCache(tenantId)
      if (!cachedTheme) {
        return false
      }

      this.tenantId = cachedTheme.tenantId
      this.themeId = cachedTheme.themeId
      this.themeName = cachedTheme.themeName
      this.themeSettings = clonePlain(cachedTheme.themeSettings)
      this.databaseThemeSettings = clonePlain(cachedTheme.databaseThemeSettings)
      this.themeTemplate = clonePlain(cachedTheme.themeTemplate)
      this.availableFonts = [...cachedTheme.availableFonts]
      this.themeUpdatedAt = cachedTheme.themeUpdatedAt
      this.lastLoadedAt = cachedTheme.lastLoadedAt
      this.lastSyncedAt = cachedTheme.lastSyncedAt
      this.isLoaded = true

      return true
    },

    persistCache() {
      if (!this.tenantId) {
        return
      }

      writeCache({
        tenantId: this.tenantId,
        themeId: this.themeId,
        themeName: this.themeName,
        themeSettings: clonePlain(this.themeSettings),
        databaseThemeSettings: clonePlain(this.databaseThemeSettings),
        themeTemplate: clonePlain(this.themeTemplate),
        availableFonts: [...this.availableFonts],
        themeUpdatedAt: this.themeUpdatedAt,
        lastLoadedAt: this.lastLoadedAt,
        lastSyncedAt: this.lastSyncedAt
      })
    },

    setThemeSettings(nextSettings: ThemeSettingsMap) {
      this.themeSettings = clonePlain(nextSettings)
      this.persistCache()
    },

    setThemeSetting(key: string, setting: Partial<ThemeSettingRecord>) {
      const existingSetting = this.themeSettings[key]
      const templateSetting = this.themeTemplate.find((entry) => entry.key === key)

      this.themeSettings = {
        ...this.themeSettings,
        [key]: {
          ...clonePlain(templateSetting ?? { key }),
          ...clonePlain(existingSetting ?? {}),
          ...clonePlain(setting),
          key
        }
      }

      this.persistCache()
    },

    setThemeValue(key: string, value: any) {
      const existingSetting = this.themeSettings[key]
      const templateSetting = this.themeTemplate.find((entry) => entry.key === key)

      this.themeSettings = {
        ...this.themeSettings,
        [key]: {
          ...clonePlain(templateSetting ?? { key }),
          ...clonePlain(existingSetting ?? {}),
          key,
          value: clonePlain(value)
        }
      }

      this.persistCache()
    },

    resetToDatabaseState() {
      this.themeSettings = clonePlain(this.databaseThemeSettings)
      this.error = null
      this.persistCache()
    },

    async loadThemeTemplate(force = false) {
      if (this.themeTemplate.length > 0 && !force) {
        return this.themeTemplate
      }

      const response = await axiosInstance.get('/theme/settings/template')
      this.themeTemplate = clonePlain(response.data ?? [])
      this.persistCache()

      return this.themeTemplate
    },

    async loadTheme(force = false) {
      const tenantId = this.currentTenantId

      if (!tenantId) {
        throw new Error('No tenant ID available for loading the theme.')
      }

      this.tenantId = tenantId

      if (this.themeTemplate.length === 0) {
        await this.loadThemeTemplate(force)
      }

      const response = await axiosInstance.get<ThemeApiResponse>(`/theme/${tenantId}`)
      const payload = response.data ?? {}
      const mergedSettings = applyTemplateDefaults(
        normalizeSettingsMap({
          ...(payload.styles ?? {}),
          ...(payload.moduleThemeSettings ?? {})
        }),
        this.themeTemplate
      )

      this.themeId = payload.id ?? payload._id ?? null
      this.themeName = payload.name ?? null
      this.availableFonts = Array.isArray(payload.availableFonts) ? [...payload.availableFonts] : []
      this.themeUpdatedAt = payload.updatedAt ?? null
      this.themeSettings = clonePlain(mergedSettings)
      this.databaseThemeSettings = clonePlain(mergedSettings)
      this.lastLoadedAt = new Date().toISOString()
      this.lastSyncedAt = this.lastLoadedAt
      this.isLoaded = true
      this.error = null
      this.persistCache()

      return this.themeSettings
    },

    async initialize(force = false) {
      const tenantId = this.currentTenantId

      if (!tenantId) {
        this.reset()
        return
      }

      const tenantChanged = this.tenantId !== tenantId
      if (tenantChanged) {
        this.reset(false)
      }

      this.tenantId = tenantId
      const alreadyLoadedForTenant = this.isLoaded && this.tenantId === tenantId

      if (!force && !alreadyLoadedForTenant) {
        this.hydrateFromCache(tenantId)
      }

      if (!force && alreadyLoadedForTenant) {
        return
      }

      this.loading = true
      this.error = null
      const shouldRefreshTemplate = force || tenantChanged || !alreadyLoadedForTenant

      try {
        await this.loadThemeTemplate(shouldRefreshTemplate)
        await this.loadTheme()
      } catch (error: any) {
        this.error = error?.response?.data?.message || error?.message || 'Failed to load theme.'
        throw error
      } finally {
        this.loading = false
      }
    },

    async refresh() {
      await this.initialize(true)
    },

    async saveTheme() {
      const tenantId = this.currentTenantId
      if (!tenantId) {
        throw new Error('No tenant ID available for saving the theme.')
      }

      this.saving = true
      this.error = null

      try {
        if (this.themeTemplate.length === 0) {
          await this.loadThemeTemplate()
        }

        const payload = buildSavePayload(this.themeSettings, this.themeTemplate)
        await axiosInstance.put(`/theme/${tenantId}`, payload)

        this.databaseThemeSettings = clonePlain(this.themeSettings)
        this.lastSyncedAt = new Date().toISOString()
        this.error = null
        this.persistCache()

        return clonePlain(this.themeSettings)
      } catch (error: any) {
        this.error = error?.response?.data?.message || error?.message || 'Failed to save theme.'
        throw error
      } finally {
        this.saving = false
      }
    },

    reset(clearTenantCache = true) {
      if (clearTenantCache) {
        removeCache(this.tenantId)
      }

      this.tenantId = null
      this.themeId = null
      this.themeName = null
      this.themeSettings = {}
      this.databaseThemeSettings = {}
      this.themeTemplate = []
      this.availableFonts = []
      this.loading = false
      this.saving = false
      this.isLoaded = false
      this.error = null
      this.themeUpdatedAt = null
      this.lastLoadedAt = null
      this.lastSyncedAt = null
    }
  }
})
